import type { ZoneStatus } from '@jq4race/shared';

/**
 * computeZoneCapacityHint — client-side LIVE PREVIEW of remaining zone
 * capacity, shown next to the capacity field while an admin creates/edits a
 * zone (location-zone-capacity-guard UX follow-up).
 *
 * This mirrors the SUM semantics of the backend's ZoneCapacityPolicy (see
 * apps/backend/src/modules/zones/domain/services/zone-capacity.policy.ts):
 * S = location.vehicleCapacity (null = unlimited -> hint hidden), T = sum of
 * `capacity` across the location's ACTIVE zones (null capacity counts as 0).
 * It intentionally does NOT reimplement the write-time
 * canWriteZoneCapacity/canUpdateLocationCapacity rules (the "don't worsen a
 * pre-existing over-allocation" escape hatch) — those only apply to the
 * actual write and would be misleading in a hint shown before submit; the
 * backend remains the single source of truth for whether a save is allowed.
 */

export interface ZoneCapacityHintZone {
  id: string;
  capacity: number | null;
  status: ZoneStatus;
}

export interface ComputeZoneCapacityHintInput {
  /** The parent location's vehicleCapacity (null = unlimited). */
  locationCapacity: number | null;
  /** All zones of the location (both statuses — inactive ones are filtered out here). */
  zones: ZoneCapacityHintZone[];
  /** When editing a zone, its id — excluded from the assigned sum so it doesn't shadow itself. */
  excludeZoneId?: string;
}

export type ZoneCapacityHint =
  | { visible: false }
  | { visible: true; status: 'ok'; total: number; assigned: number; remaining: number }
  | { visible: true; status: 'over'; total: number; assigned: number; overBy: number };

export function computeZoneCapacityHint({
  locationCapacity,
  zones,
  excludeZoneId,
}: ComputeZoneCapacityHintInput): ZoneCapacityHint {
  if (locationCapacity === null) return { visible: false };

  const assigned = zones
    .filter((z) => z.status === 'active' && z.id !== excludeZoneId)
    .reduce((sum, z) => sum + (z.capacity ?? 0), 0);

  const remaining = locationCapacity - assigned;

  if (remaining < 0) {
    return { visible: true, status: 'over', total: locationCapacity, assigned, overBy: -remaining };
  }
  return { visible: true, status: 'ok', total: locationCapacity, assigned, remaining };
}
